import { useMutation } from '@apollo/client';
import { useI18n } from '@i18n';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GetNotificationsQuery } from '@predium/client-graphql';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { ICONS } from '@/assets/icons';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import Iconify from '@/components/Iconify';
import MenuPopover from '@/components/MenuPopover';
import { useDownloads } from '@/components/notifications/DownloadsContext';
import DownloadsTab from '@/components/notifications/DownloadsTab';
import { SnackbarTimeouts } from '@/components/NotistackProvider';
import Scrollbar from '@/components/Scrollbar';
import { READ_NOTIFICATION } from '@/graphql/Downloads.queries';
import { durationFromNow } from '@/utils/formatTime';

type Props = {
  notifications: GetNotificationsQuery['notification'];
};

enum TabValue {
  NOTIFICATIONS = 'notifications',
  DOWNLOADS = 'downloads',
}

/**
 * Button that fits the NavBar. Clicking the button shows a Popover Menu with a list of notifications.
 * These notifications are ordered for read/unread and recency.
 */
export default function NotificationsPopover({ notifications }: Props) {
  const { t } = useI18n();
  const { enqueueSnackbar } = useSnackbar();
  const [currentTab, setCurrentTab] = useState<TabValue>(TabValue.NOTIFICATIONS);
  const [unseenNotificationsCount, setUnseenNotificationsCount] = useState(
    notifications.filter((item) => !item.read).length,
  );

  const {
    popoverAnchorEl,
    closePopover,
    shouldShowDownloadsTab,
    setShouldShowDownloadsTab,
    isPopoverOpen,
    openDownloadsPopover,
  } = useDownloads();

  const [markNotifications] = useMutation(READ_NOTIFICATION, {
    onError: () =>
      enqueueSnackbar(t('NotificationPopover_ReadNotification-Error'), {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      }),
  });

  const readNotifications = notifications.filter((item) => item.read);
  const unreadNotifications = notifications.filter((item) => !item.read);
  const totalUnReadCount = unreadNotifications.length;

  useEffect(() => {
    if (shouldShowDownloadsTab && isPopoverOpen) {
      setCurrentTab(TabValue.DOWNLOADS);
      setShouldShowDownloadsTab(false);
    }
  }, [shouldShowDownloadsTab, setShouldShowDownloadsTab, isPopoverOpen]);

  const handleOpenPopover = useCallback(() => {
    if (isPopoverOpen) {
      closePopover();
    } else {
      openDownloadsPopover();
    }
  }, [closePopover, isPopoverOpen, openDownloadsPopover]);

  const handleMarkAllAsRead = useCallback(() => {
    markNotifications({
      variables: {
        notificationIds: unreadNotifications.map((notification) => notification.id),
      },
    });
  }, [markNotifications, unreadNotifications]);

  const readSingleNotification = useCallback(
    (notificationId: number) => {
      markNotifications({
        variables: {
          notificationIds: [notificationId],
        },
      });
    },
    [markNotifications],
  );

  // Handle tab change and mark appropriate items as seen
  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: TabValue) => {
    setCurrentTab(newValue);
  }, []);

  useEffect(() => {
    if (currentTab === TabValue.NOTIFICATIONS && isPopoverOpen) {
      setUnseenNotificationsCount(0);
    }
  }, [currentTab, isPopoverOpen]);

  const totalBadgeCount = totalUnReadCount;

  return (
    <>
      <IconButton
        ref={popoverAnchorEl}
        color={isPopoverOpen ? 'primary' : 'default'}
        onClick={handleOpenPopover}
        sx={{ width: 40, height: 40 }}
      >
        <Badge badgeContent={totalBadgeCount} color="error" data-testid="bell-icon-badge">
          <Iconify icon={ICONS.MDI_BELL_OUTLINE} />
        </Badge>
      </IconButton>

      <MenuPopover
        open={isPopoverOpen}
        anchorEl={popoverAnchorEl.current}
        onClose={closePopover}
        sx={{ width: 360, p: 0, overflow: 'hidden' }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            value={TabValue.NOTIFICATIONS}
            sx={{ mr: '0 !important' }}
            data-testid="notifications-tab"
            label={
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                <span>{t('General_Notifications')}</span>
                {unseenNotificationsCount > 0 && (
                  <Badge
                    badgeContent={unseenNotificationsCount}
                    color="error"
                    sx={{
                      '& .MuiBadge-badge': {
                        position: 'static',
                        transform: 'none',
                        fontSize: '0.75rem',
                        height: 16,
                        minWidth: 16,
                      },
                    }}
                  />
                )}
              </Stack>
            }
          />
          <Tab
            value={TabValue.DOWNLOADS}
            sx={{ mr: '0 !important' }}
            data-testid="downloads-tab"
            label={
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                <span>{t('Downloads')}</span>
              </Stack>
            }
          />
        </Tabs>

        {currentTab === TabValue.NOTIFICATIONS && (
          <>
            {/* Only show header section if there are notifications */}
            {totalUnReadCount > 0 && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 2,
                    pl: 2.5,
                    pr: 1,
                  }}
                >
                  <div>
                    {totalUnReadCount > 0 && (
                      <PTypography variant={'body2'} color="secondary">
                        {t('NotificationPopover_UnreadMessagesTitle', { count: totalUnReadCount })}
                      </PTypography>
                    )}
                  </div>

                  {totalUnReadCount > 0 && (
                    <Tooltip title={t('NotificationPopover_MarkMessagesAsRead')}>
                      <IconButton color="primary" onClick={handleMarkAllAsRead} data-testid="mark-all-read-button">
                        <Iconify icon={ICONS.EVA_DONE_ALL_FILL} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <Divider sx={{ borderStyle: 'dashed' }} />
              </>
            )}

            <Scrollbar sx={{ maxHeight: 340 }}>
              {unreadNotifications.length > 0 && (
                <List
                  disablePadding
                  subheader={
                    <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                      {t('General_New')}
                    </ListSubheader>
                  }
                >
                  {unreadNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      readSingleNotification={readSingleNotification}
                    />
                  ))}
                </List>
              )}

              {readNotifications.length > 0 && (
                <List
                  disablePadding
                  subheader={
                    <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                      {t('NotificationPopover_Read')}
                    </ListSubheader>
                  }
                >
                  {readNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      readSingleNotification={readSingleNotification}
                    />
                  ))}
                </List>
              )}

              {totalUnReadCount === 0 && readNotifications.length === 0 && (
                <ListSubheader disableSticky sx={{ py: 1, px: 2.5, typography: 'overline' }}>
                  {t('NotificationPopover_NoNotificationsAvailable')}
                </ListSubheader>
              )}
            </Scrollbar>
          </>
        )}

        {currentTab === TabValue.DOWNLOADS && <DownloadsTab />}
      </MenuPopover>
    </>
  );
}

type NotificationItemProps = {
  notification: GetNotificationsQuery['notification'][number];
  readSingleNotification: (notificationId: number) => void;
};

/**
 * Shows a single notification that is clickable if it is unread or a link.
 * Clicking the notification will mark it as read. A different end icon is displayed on the type of notification.
 */
function NotificationItem({ notification, readSingleNotification }: NotificationItemProps) {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();

  const { language } = useI18n();

  const isUnread = !notification.read;

  const getActionButton = () => {
    if (notification.notification_template.href) {
      return (
        <IconButton
          edge="end"
          aria-label="comments"
          href={notification.notification_template.href}
          onClick={() => readSingleNotification(notification.id)}
          target="_blank"
        >
          <Iconify icon={ICONS.MINGCUTE_EXTERNAL_LINK_LINE} width={25} height={25} />
        </IconButton>
      );
    } else return null;
  };

  const description = notification.notification_template.description;
  const shouldTruncate = description.length > 500;

  const content = (
    <ListItemText
      disableTypography
      // description should get whole width minus the end icon and some padding if there is an icon.
      sx={{ width: `calc(100% - ${notification.notification_template.href ? '30px' : '0'})`, pl: 2 }}
      primary={
        <>
          {isUnread ? (
            <Iconify
              icon={ICONS.PH_DOT_OUTLINE_FILL}
              width={35}
              height={35}
              color={theme.palette.info.main}
              sx={{ position: 'absolute', left: 8, top: 6 }}
            />
          ) : null}
          <PTypography variant={'subtitle2'}>{notification.notification_template.title}</PTypography>

          <PTypography variant={'body2'} color="secondary">
            {shouldTruncate && !expanded ? description.substring(0, 500) + '...' : description}

            {shouldTruncate ? (
              <IconButton sx={{ padding: 0, marginLeft: 1 }} onClick={() => setExpanded(!expanded)}>
                {expanded ? (
                  <Iconify icon={ICONS.MATERIAL_SYMBOLS_ARROW_DROP_UP_ROUNDED} />
                ) : (
                  <Iconify icon={ICONS.MATERIAL_SYMBOLS_ARROW_DROP_DOWN_ROUNDED} />
                )}
              </IconButton>
            ) : null}
          </PTypography>
        </>
      }
      secondary={
        <Stack direction="row" sx={{ mt: 0.5, typography: 'caption', color: 'text.disabled', alignItems: 'center' }}>
          <Iconify icon={ICONS.EVA_CLOCK_FILL} width={16} sx={{ mr: 0.5 }} />
          <PTypography variant={'caption'}>{durationFromNow(notification.created_at, language)}</PTypography>
        </Stack>
      }
    />
  );

  return (
    <ListItem secondaryAction={getActionButton()} disablePadding>
      {isUnread ? (
        <ListItemButton
          onClick={() => {
            if (isUnread) {
              readSingleNotification(notification.id);
            }
          }}
          sx={{
            py: 1.5,
            px: 2.5,
            mt: '1px',
            ...(isUnread && {
              bgcolor: 'action.selected',
            }),
          }}
        >
          {content}
        </ListItemButton>
      ) : (
        <Box
          sx={{
            py: 1.5,
            px: 2.5,
            mt: '1px',
            width: '100%',
          }}
        >
          {content}
        </Box>
      )}
    </ListItem>
  );
}
